<template>
  <soha-page-loading v-if="loading" />
  <div class="card card-custom gutter-b example example-compact p-8" v-else>
    <div class="row m-0">
      <span class="soha-page-title">
        {{
          editable
            ? $t(`${I18nPrefix}EDIT_PAGE_TITLE`)
            : $t(`${I18nPrefix}CREATE_PAGE_TITLE`)
        }}
      </span>
    </div>
    <div class="row mx-0 mt-10">
      <div class="col-12">
        <soha-text-field
          name="checkListTitle"
          :placeholder="$t(`${I18nPrefix}TITLE`)"
          class="w-100"
          v-model="title.value"
          :error="title.error"
        />
      </div>
      <div class="col-12">
        <soha-text-area
          name="checkListDescription"
          class="w-100"
          v-model="description"
          :placeholder="$t(`${I18nPrefix}DESCRIPTION`)"
        />
      </div>
    </div>

    <div class="row m-0">
      <div class="col-12 text-center" v-if="items.length === 0">
        <soha-no-data :message="$t(`${I18nPrefix}EMPTY_ITEMS_LIST`)" />
      </div>
      <div v-else class="col-12">
        <h5 class="my-5">{{ $t(`${I18nPrefix}ITEMS_CHECK_LIST`) }}</h5>
        <div class="col-12 my-4" v-for="(item, index) in items" :key="index">
          <div class="soha-check-list-card">
            <div class="soha-check-list-card-header">
              <div class="d-flex col-10 p-0 soha-manage-name">
                <span class="pr-2 d-inline-block" style="font-size: 1.231rem">
                  {{ toPersianDigits(index + 1) + "- " }}
                </span>
                <span
                  class="soha-manage-name d-inline-block"
                  v-html="item.title"
                />
              </div>

              <soha-menu class="col-2 p-0 d-flex justify-content-start">
                <template v-slot:activator>
                  <span name="manageCheckListMenu" class="cursor-pointer">
                    <inline-svg src="/media/svg/soha/btn-icons/menu.svg" />
                  </span>
                </template>
                <soha-list>
                  <soha-list-item
                    class="primary-color"
                    :text="$t(`${I18nPrefix}EDIT`)"
                    icon="edit"
                    name="editItem"
                    @click="openEditItemModal(item, index)"
                  />
                  <soha-list-item
                    :text="$t(`${I18nPrefix}DELETE`)"
                    icon="delete"
                    @click="items.splice(index, 1)"
                    name="removeItem"
                  />
                </soha-list>
              </soha-menu>
            </div>
            <div class="row m-0">
              <div class="soha-message-primary-text p-0">
                <span class="font-size-sm">
                  {{ $t(`${I18nPrefix}DESCRIPTION`) }} :
                </span>
                {{ item.description ? item.description : "-" }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row m-0 px-8">
      <soha-add-new-item
        :text="$t(`${I18nPrefix}ADD_ITEM`)"
        @addNewItem="$bvModal.show('manage-check-list')"
      />
    </div>

    <div class="row mx-0 mt-10 justify-content-end">
      <soha-button
        name="insertEditCheckList"
        @click="editable ? editCheckList() : insertCheckList()"
        :loading="btnLoading"
        :text="editable ? $t(`${I18nPrefix}EDIT`) : $t(`${I18nPrefix}CREATE`)"
      />
    </div>

    <soha-modal
      id="manage-check-list"
      @hide="clearDataModal"
      :title="
        itemEdit ? $t(`${I18nPrefix}EDIT_ITEM`) : $t(`${I18nPrefix}ADD_ITEM`)
      "
    >
      <template v-slot:body>
        <div class="row m-0">
          <div class="col-12">
            <div class="col-12 px-0 mt-5">
              <soha-text-area
                name="itemTitle"
                :placeholder="$t(`${I18nPrefix}TITLE`)"
                class="w-100"
                v-model="itemTitle.value"
                :error="itemTitle.error"
              />
            </div>
            <div class="col-12 px-0 mt-5">
              <soha-text-area
                name="itemDescription"
                :placeholder="$t(`${I18nPrefix}DESCRIPTION`)"
                class="w-100"
                v-model="itemDescription"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <soha-button
          name="manageCheckList"
          @click="itemEdit ? editItem(selectedItemIndex) : addItem()"
          :text="itemEdit ? $t(`${I18nPrefix}EDIT`) : $t(`${I18nPrefix}INSERT`)"
        />
      </template>
    </soha-modal>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import ValidationMixin from "@/mixins/ValidationMixin";
import UIMixin from "@/mixins/UIMixin";
import { mapGetters } from "vuex";

export default {
  name: "CheckListCreateEdit",

  mixins: [ValidationMixin, UIMixin],

  data() {
    return {
      I18nPrefix: "PAGES.SETTING.CREATE_EDIT_CHECK_LIST.",
      loading: false,
      btnLoading: false,
      itemEdit: false,
      title: {
        value: null,
        error: null,
      },
      description: null,
      itemTitle: {
        value: null,
        error: null,
      },
      itemDescription: null,
      selectedItemIndex: null,
      items: [],
      checkListId: null,
      editable: false,
    };
  },

  mounted() {
    const { params, query } = this.$route;
    this.checkListId = params.id || query.id;
    this.editable = !!query.id;
    if (this.checkListId) this.getCheckListInfo();
  },

  methods: {
    clearDataModal() {
      this.itemTitle = {
        value: null,
        error: null,
      };
      this.itemDescription = null;
    },

    addItem() {
      this.itemTitle.error = this.validateField(
        this.itemTitle.value,
        this.$t(`${this.I18nPrefix}TITLE`)
      );
      if (this.itemTitle.error) return;
      this.items.push({
        title: this.itemTitle.value,
        description: this.itemDescription,
        id: null,
      });
      this.$bvModal.hide("manage-check-list");
    },

    editItem(index) {
      this.itemTitle.error = this.validateField(
        this.itemTitle.value,
        this.$t(`${this.I18nPrefix}TITLE`)
      );
      if (this.itemTitle.error) return;
      this.items[index].title = this.itemTitle.value;
      this.items[index].description = this.itemDescription;
      this.$bvModal.hide("manage-check-list");
    },

    openEditItemModal(item, index) {
      this.itemTitle.value = item.title;
      this.itemDescription = item.description;
      this.selectedItemIndex = index;
      this.itemEdit = true;
      this.$bvModal.show("manage-check-list");
    },

    insertCheckList() {
      this.title.error = this.validateField(
        this.title.value,
        this.$t(`${this.I18nPrefix}TITLE`)
      );
      if (this.title.error) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (this.items.length === 0) {
        this.errorToast(this.$t(`${this.I18nPrefix}EMPTY_ITEMS_LIST`));
        return;
      }
      this.btnLoading = true;
      ApiService.post("api/v2/checklists", {
        title: this.title.value,
        description: this.description,
        items: this.items,
        companyId: this.companyId,
      }).then((data) => {
        if (+data.data.code === 201) {
          this.successToast(data.data.message);
          this.$router.push({ name: "checklist-setting" });
        } else {
          this.errorToast(data.data.message);
        }
        this.btnLoading = false;
      });
    },

    editCheckList() {
      this.title.error = this.validateField(
        this.title.value,
        this.$t(`${this.I18nPrefix}TITLE`)
      );
      if (this.title.error) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (this.items.length === 0) {
        this.errorToast(this.$t(`${this.I18nPrefix}EMPTY_ITEMS_LIST`));
        return;
      }
      this.btnLoading = true;
      ApiService.put(`api/v2/checklists/${this.checkListId}`, {
        title: this.title.value,
        description: this.description,
        items: this.items,
      }).then((data) => {
        if (+data.data.code === 204) {
          this.successToast(data.data.message);
          this.$router.push({ name: "checklist-setting" });
        } else {
          this.errorToast(data.data.message);
        }
        this.btnLoading = false;
      });
    },

    getCheckListInfo() {
      this.loading = true;
      ApiService.get(`api/v2/checklists/${this.checkListId}`).then((data) => {
        if (!data.data.code) {
          this.items = data.data.items;
          this.title.value = this.editable ? data.data.title : null;
          this.description = data.data.description;
        } else {
          this.errorToast(data.data.message);
        }
        this.loading = false;
      });
    },
  },

  computed: {
    ...mapGetters(["companyId", "canDo"]),
  },
};
</script>

<style scoped>
.soha-check-list-card {
  border: 0.077rem solid var(--box-border-color);
  border-radius: 0.462rem;
  padding: 0.769rem 1.154rem;
  height: 100%;
}
.soha-check-list-card-header {
  display: flex;
  align-items: center;
  border-bottom: 0.077rem solid var(--box-border-color);
  padding-bottom: 0.769rem;
  margin-bottom: 0.769rem;
  color: var(--primary-color);
}
.soha-manage-name {
  color: var(--primary-color);
  font-weight: bold;
  text-align: right;
}
.soha-message-primary-text {
  color: var(--primary-color);
}
</style>
